import * as THREE from 'three'

/**
 * 三维包围盒的日志友好快照。
 */
export interface ProjectModelBoxDiagnostics {
  /** 包围盒最小点。 */
  readonly min: readonly [number, number, number]
  /** 包围盒最大点。 */
  readonly max: readonly [number, number, number]
  /** 包围盒中心点。 */
  readonly center: readonly [number, number, number]
  /** 包围盒尺寸。 */
  readonly size: readonly [number, number, number]
  /** 包围盒对角线长度。 */
  readonly diagonal: number
}

/**
 * 单个 mesh 的调试快照。
 */
export interface ProjectModelMeshDiagnostics {
  /** mesh 名称，缺失时使用对象类型。 */
  readonly name: string
  /** mesh 的 THREE uuid。 */
  readonly uuid: string
  /** mesh 类型。 */
  readonly type: string
  /** 对象自身 visible 状态。 */
  readonly visible: boolean
  /** 父链是否全部 visible。 */
  readonly parentChainVisible: boolean
  /** 材质是否允许渲染。 */
  readonly materialRenderable: boolean
  /** 图层 mask，排查相机 layer 不匹配时使用。 */
  readonly layerMask: number
  /** 世界坐标位置。 */
  readonly worldPosition: readonly [number, number, number]
  /** 世界缩放。 */
  readonly worldScale: readonly [number, number, number]
  /** mesh 自身世界包围盒。 */
  readonly worldBounds: ProjectModelBoxDiagnostics | null
}

/**
 * 项目模型组合结果的场景诊断。
 */
export interface ProjectModelSceneGraphSummary {
  /** 场景子树中的 Object3D 总数。 */
  readonly objectCount: number
  /** 场景子树中的 mesh 总数。 */
  readonly meshCount: number
  /** mesh 自身 visible 为 true 的数量。 */
  readonly visibleMeshCount: number
  /** 父链和材质都允许渲染的 mesh 数量。 */
  readonly renderableMeshCount: number
  /** 项目 root 的完整包围盒。 */
  readonly rootWorldBounds: ProjectModelBoxDiagnostics | null
  /** 只由可渲染 mesh 汇总出的包围盒。 */
  readonly renderableMeshWorldBounds: ProjectModelBoxDiagnostics | null
  /** 前几个 mesh 样本，用于快速判断 transform、材质和父链状态。 */
  readonly meshSamples: readonly ProjectModelMeshDiagnostics[]
}

const DEFAULT_MESH_SAMPLE_LIMIT = 8
const DIAGNOSTIC_NUMBER_PRECISION = 6

/**
 * 把数值收敛为日志中可读的有限小数。
 *
 * @param value 原始数值。
 * @returns 有限数值的短小数表示；非有限值保持原值，便于暴露异常。
 */
function roundDiagnosticNumber(value: number): number {
  if (!Number.isFinite(value)) {
    return value
  }
  return Number(value.toFixed(DIAGNOSTIC_NUMBER_PRECISION))
}

/**
 * 将 Vector3 转换为稳定元组。
 *
 * @param vector 三维向量。
 * @returns 适合日志输出的三元组。
 */
function vectorToDiagnosticsTuple(vector: THREE.Vector3): readonly [number, number, number] {
  return [
    roundDiagnosticNumber(vector.x),
    roundDiagnosticNumber(vector.y),
    roundDiagnosticNumber(vector.z),
  ] as const
}

/**
 * 将 Box3 转换为日志快照。
 *
 * @param box Three.js 包围盒。
 * @returns 非空包围盒的快照；空包围盒返回 null。
 */
function boxToDiagnostics(box: THREE.Box3): ProjectModelBoxDiagnostics | null {
  if (box.isEmpty()) {
    return null
  }

  const center = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())
  return {
    min: vectorToDiagnosticsTuple(box.min),
    max: vectorToDiagnosticsTuple(box.max),
    center: vectorToDiagnosticsTuple(center),
    size: vectorToDiagnosticsTuple(size),
    diagonal: roundDiagnosticNumber(size.length()),
  }
}

/**
 * 判断材质是否允许 mesh 被渲染。
 *
 * @param material mesh 材质。
 * @returns 材质可见、透明度非零且允许写颜色时返回 true。
 */
function isMaterialRenderable(material: THREE.Material | readonly THREE.Material[] | null | undefined): boolean {
  if (!material) {
    return false
  }

  const materials = Array.isArray(material) ? material : [material]
  return materials.some((item) => {
    const colorWritable = (item as THREE.Material & { colorWrite?: boolean }).colorWrite
    const opacity = (item as THREE.Material & { opacity?: number }).opacity
    return item.visible !== false
      && colorWritable !== false
      && (typeof opacity !== 'number' || opacity > 0)
  })
}

/**
 * 判断对象父链是否全部可见。
 *
 * @param object 起点对象。
 * @returns 当前对象和所有父对象 visible 均为 true 时返回 true。
 */
function isParentChainVisible(object: THREE.Object3D): boolean {
  let current: THREE.Object3D | null = object
  while (current) {
    if (!current.visible) {
      return false
    }
    current = current.parent
  }
  return true
}

/**
 * 创建单个 mesh 的诊断快照。
 *
 * @param mesh 目标 mesh。
 * @returns mesh 诊断信息。
 */
function createMeshDiagnostics(mesh: THREE.Mesh): ProjectModelMeshDiagnostics {
  const worldPosition = mesh.getWorldPosition(new THREE.Vector3())
  const worldScale = mesh.getWorldScale(new THREE.Vector3())
  const worldBounds = new THREE.Box3().setFromObject(mesh)

  return {
    name: mesh.name.trim() || mesh.type,
    uuid: mesh.uuid,
    type: mesh.type,
    visible: mesh.visible,
    parentChainVisible: isParentChainVisible(mesh),
    materialRenderable: isMaterialRenderable(mesh.material),
    layerMask: mesh.layers.mask,
    worldPosition: vectorToDiagnosticsTuple(worldPosition),
    worldScale: vectorToDiagnosticsTuple(worldScale),
    worldBounds: boxToDiagnostics(worldBounds),
  }
}

/**
 * 判断 mesh 是否应进入“真实可渲染包围盒”。
 *
 * @param mesh 目标 mesh。
 * @returns mesh 父链、材质与 geometry 均可渲染时返回 true。
 */
function isRenderableMesh(mesh: THREE.Mesh): boolean {
  return isParentChainVisible(mesh)
    && isMaterialRenderable(mesh.material)
    && Boolean(mesh.geometry)
}

/**
 * 汇总项目模型组合后的场景对象状态。
 *
 * @param root 项目模型组合出的 `imported-assembly` 根对象。
 * @param sampleLimit mesh 样本数量上限。
 * @returns 场景图规模、可渲染 mesh 包围盒与样本诊断。
 */
export function summarizeProjectModelSceneGraph(
  root: THREE.Object3D,
  sampleLimit = DEFAULT_MESH_SAMPLE_LIMIT,
): ProjectModelSceneGraphSummary {
  root.updateMatrixWorld(true)

  let objectCount = 0
  let meshCount = 0
  let visibleMeshCount = 0
  let renderableMeshCount = 0
  const renderableBounds = new THREE.Box3()
  const meshSamples: ProjectModelMeshDiagnostics[] = []

  root.traverse((object) => {
    objectCount += 1
    if (!(object instanceof THREE.Mesh)) {
      return
    }

    meshCount += 1
    if (object.visible) {
      visibleMeshCount += 1
    }
    if (meshSamples.length < sampleLimit) {
      meshSamples.push(createMeshDiagnostics(object))
    }
    if (isRenderableMesh(object)) {
      renderableMeshCount += 1
      renderableBounds.union(new THREE.Box3().setFromObject(object))
    }
  })

  return {
    objectCount,
    meshCount,
    visibleMeshCount,
    renderableMeshCount,
    rootWorldBounds: boxToDiagnostics(new THREE.Box3().setFromObject(root)),
    renderableMeshWorldBounds: boxToDiagnostics(renderableBounds),
    meshSamples,
  }
}
